import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Card } from 'primeng/card';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { Router } from '@angular/router';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RuleCreateFormModel } from './form-group/rule-create.model';
import { RuleFormBuilder } from './form-group/rule-create.form.builder';
import { BaseFormComponent } from '../../../../../core/components/base-form.component';
import { MessageService } from 'primeng/api';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import {
  Regulamin,
  RegulaminRequestBody,
} from '../../../../../shared/models/regulamin';
import { RulesApi } from '../../../../../shared/services/api/rules.api';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../../../../core/services/http-error-response.service';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';
import { FormsUtils } from '../../../../../shared/utils/forms.utils';

@Component({
  selector: 'app-rule-add',
  imports: [
    Card,
    DatePicker,
    Button,
    ControlErrorDirective,
    EditorModule,
    FormsModule,
    ReactiveFormsModule,
    RequiredLabelDirective,
  ],
  providers: [RulesApi],
  templateUrl: './rule-add.html',
  styleUrl: './rule-add.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RuleAddComponent extends BaseFormComponent<RuleCreateFormModel> {
  protected readonly isSubmitting = signal(false);

  readonly #router = inject(Router);
  readonly #messageService = inject(MessageService);
  readonly #rulesApi = inject(RulesApi);
  #httpResponseService = inject(HttpResponseService);

  protected override createFormGroup(): FormGroup<RuleCreateFormModel> {
    return RuleFormBuilder.createFormGroup();
  }

  #handleSuccess(response: Regulamin): void {
    this.#httpResponseService.showSuccessMessage(
      'Dodano nową wersję regulaminu',
    );

    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}`,
    ]);
  }

  protected onCancel(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}`,
    ]);
  }

  protected onFormSubmit(): void {
    if (!FormsUtils.isReadyForSave(this.formGroup())) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Brak wymaganych danych',
        detail: 'Uzupełnij wymagane pola w formularzu.',
        life: 4000,
      });
      return;
    }

    const value = this.formGroup().getRawValue();

    const payload: RegulaminRequestBody = {
      dataOd: value.dataOd,
      tresc: value.tresc!,
    };

    this.isSubmitting.set(true);
    this.#rulesApi
      .createRule$(payload)
      .pipe(
        tap((response) => {
          this.#handleSuccess(response);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas dodawania regulaminu',
            HttpResponseErrorSource.MESSAGE_FROM_RESPONSE,
          );
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }
}
